import {
  Box,
  Text,
  Circle,
  VStack,
  useBreakpointValue,
} from '@chakra-ui/react';

interface MilestoneProps {
  pos: string;
  val: string;
  active: boolean;
  color: string;
  reward?: boolean;
}

const Milestone = ({
  pos,
  val,
  active,
  color,
  reward = true,
}: MilestoneProps) => {
  // Two tiers — small below hxl, large at hxl+
  const dollarSize = useBreakpointValue({ base: '12px', hxl: '18px' });
  const dollarFontSize = useBreakpointValue({ base: '7px', hxl: '11px' });
  const labelFontSize = useBreakpointValue({ base: '9px', hxl: '11px' });
  const triangleWidth = useBreakpointValue({ base: '3px', hxl: '3px' });
  const triangleHeight = useBreakpointValue({ base: '3px', hxl: '4px' });
  const dollarBottom = useBreakpointValue({ base: '18px', hxl: '24px' });
  const triangleTop = useBreakpointValue({ base: '18px', hxl: '22px' });

  return (
    <>
      {active && reward && (
        <Box
          position="absolute"
          left={pos}
          bottom={dollarBottom}
          transform="translateX(-50%)"
          zIndex={2}
        >
          {(() => {
            const CircleComponent = Circle as any;
            return (
              <CircleComponent
                size={dollarSize}
                fontWeight="900"
                fontSize={dollarFontSize}
                color="white"
                bg="linear-gradient(135deg, #22c55e 0%, #15803d 100%)"
                boxShadow="0 0 15px rgba(34, 197, 94, 0.8), inset 0 0 4px rgba(255, 255, 255, 0.4)"
                position="relative"
                overflow="hidden"
                _after={{
                  content: '""',
                  position: 'absolute',
                  top: '-50%',
                  left: '-50%',
                  width: '200%',
                  height: '200%',
                  background:
                    'linear-gradient(rgba(255,255,255,0.4) 0%, rgba(255,255,255,0) 50%)',
                  transform: 'rotate(30deg)',
                }}
              >
                <Text position="relative" zIndex={3}>
                  $
                </Text>
              </CircleComponent>
            );
          })()}
        </Box>
      )}

      <Box
        position="absolute"
        left={pos}
        top={triangleTop}
        transform="translateX(-50%)"
        zIndex={2}
      >
        <VStack spacing={0} align="center">
          <Box
            width="0"
            height="0"
            borderLeft={`${triangleWidth} solid transparent`}
            borderRight={`${triangleWidth} solid transparent`}
            borderBottom={`${triangleHeight} solid ${color}`}
            mb="2px"
          />
          <Text
            fontSize={labelFontSize}
            fontWeight="bold"
            color={color}
            opacity={active ? 1 : 0.75}
          >
            {val}
          </Text>
        </VStack>
      </Box>
    </>
  );
};

export const ProgressBar = ({ label, percentage, color }: any) => {
  // Two tiers — small below hxl, large at hxl+
  const labelFontSize = useBreakpointValue({ base: '12px', hxl: '13px' });
  const barHeight = useBreakpointValue({ base: '16px', hxl: '20px' });
  const fillFontSize = useBreakpointValue({ base: '10px', hxl: '13px' });
  const fillMarginR = useBreakpointValue({ base: '10px', hxl: '12px' });
  const indicatorSize = useBreakpointValue({ base: '20px', hxl: '24px' });
  const indicatorOff = useBreakpointValue({ base: '-10px', hxl: '-12px' });
  const pt = useBreakpointValue({ base: '2', hxl: '3' });
  const pb = useBreakpointValue({ base: '4', hxl: '5' });

  const getWeightedWidth = (pct: number) => {
    if (pct <= 10) return 5 + (pct / 100) * 5;
    if (pct <= 100) return 10 + ((pct - 10) / 90) * 45;
    if (pct <= 110) return 55 + ((pct - 100) / 10) * 10;
    if (pct <= 125) return 65 + ((pct - 110) / 15) * 13;
    if (pct <= 150) return 78 + ((pct - 125) / 25) * 17;
    return 99.5;
  };

  const fillWidth = `${getWeightedWidth(percentage)}%`;

  return (
    <Box w="full" pt={pt} pb={pb} pr="10px">
      <Box mb={1} fontSize={labelFontSize}>
        {label}
      </Box>
      <Box
        position="relative"
        w="full"
        h={barHeight}
        bg="gray.300"
        borderRadius="full"
        border="1px solid"
        borderColor="gray.200"
      >
        <Milestone
          pos="55%"
          val="100%"
          active={percentage >= 100}
          color={color}
          reward={false}
        />
        <Milestone
          pos="65%"
          val="110%"
          active={percentage >= 110}
          color={color}
        />
        <Milestone
          pos="78%"
          val="125%"
          active={percentage >= 125}
          color={color}
        />
        <Milestone
          pos="95%"
          val="150%"
          active={percentage >= 150}
          color={color}
        />

        <Box
          h="full"
          bg={color}
          w={fillWidth}
          borderRadius="full"
          transition="width 1s ease-out"
          position="relative"
          display="flex"
          alignItems="center"
          justifyContent="flex-end"
        >
          <Text
            fontSize={fillFontSize}
            fontWeight={900}
            color="white"
            mr={fillMarginR}
            zIndex={2}
          >
            {percentage}%
          </Text>
          {(() => {
            const CircleComponent = Circle as any;
            return (
              <CircleComponent
                bg="white"
                size={indicatorSize}
                boxShadow="md"
                borderWidth="2px"
                borderColor={color}
                position="absolute"
                right={indicatorOff}
                zIndex={3}
              />
            );
          })()}
        </Box>
      </Box>
    </Box>
  );
};
